import { Component, OnInit } from "@angular/core";
import { UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { GridComponent } from "@progress/kendo-angular-grid";
import { Observable } from "rxjs";
import { first, map, switchMap, tap } from "rxjs/operators";
import { I18nService } from "src/app/i18n/services";
import { DateUtility } from "src/app/interfaces/date";
import { NotificationFacade } from "src/app/notifications/services/notification.facade";
import { CirculationFacade } from "src/app/services/circulation.facade";
import { StepFacade } from "src/app/step/services/step.facade";
import { DueDateInRangeValidator } from "src/app/step/validators/dueDateInRange.validator";
import { Circulation, CirculationId, IStepInit } from "../../interfaces";
import { LogClass, Logger } from "../../logger";
import { NavigationService } from "../../services";

/* ToDo DueDate rework needed */
@Component({
    selector: "circ-reorder-steps",
    templateUrl: "./reorder-steps.component.html",
    styleUrls: ["./reorder-steps.component.scss"],
    standalone: false
})
@LogClass({ source: "ReorderStepsComponent" })
export class ReorderStepsComponent implements OnInit {
  public minDueDate = DateUtility.getTodayMin();

  public errors: string[] = [];
  public loading = true;
  public hasErrors = false;
  public steps: SortableStep[] = [];

  private logger: Logger;
  public circulation$: Observable<Circulation>;
  private formGroup: UntypedFormGroup;
  private editedRowIndex: number | null;
  private circulationId: CirculationId;

  constructor(
    private circulationFacade: CirculationFacade,
    private stepFacade: StepFacade,
    private navigate: NavigationService,
    private i18nService: I18nService,
    private notificationFacade: NotificationFacade
  ) {
    this.logger.debug("ctor");
  }

  public ngOnInit(): void {
    this.circulation$ = this.circulationFacade.getCurrentCirculation();

    //Set circulationId
    const circulationId$ = this.circulation$.pipe(map((c) => c.id));
    circulationId$.pipe(first()).subscribe((id) => (this.circulationId = id));

    const orderedSteps$ = circulationId$.pipe(
      switchMap((circulationId) =>
        this.stepFacade.getStepsForCirculation(circulationId)
      ),
      tap((steps) => this.logger.info("loaded " + steps.length + " steps.")),
      map((steps) => steps),
      map((steps) =>
        steps.filter((s) => {
          return s.canEdit && s.props.status === "Waiting";
        })
      ),
      map((steps) =>
        steps.sort((a, b) => {
          return a.props.stepNo - b.props.stepNo;
        })
      ),
      map((steps) => {
        let order = 0;
        return steps.map((s) => {
          order++;
          return {
            ...s.props,
            order: order,
            originalOrder: order,
            validOrder: {}
          };
        });
      })
    );

    orderedSteps$.pipe(first()).subscribe((steps) => {
      this.steps = steps;
      this.loading = false;
      this.checkForErrors();
    });
  }

  public editHandler({
    sender,
    rowIndex,
    dataItem
  }: {
    sender: GridComponent;
    rowIndex: number;
    dataItem: SortableStep;
  }): void {
    this.closeEditor(sender);

    if (dataItem.dueDate) {
      this.formGroup = new UntypedFormGroup({
        dueDate: new UntypedFormControl(
          dataItem.dueDate,
          Validators.required
          /*, specialRangeValidator */
        ),
        order: new UntypedFormControl(dataItem.order, Validators.required)
      });
    } else {
      this.formGroup = new UntypedFormGroup({
        order: new UntypedFormControl(dataItem.order, Validators.required)
      });
    }

    this.editedRowIndex = rowIndex;

    sender.editRow(rowIndex, this.formGroup);
  }

  public cancelHandler({
    sender,
    rowIndex
  }: {
    sender: GridComponent;
    rowIndex: number;
  }): void {
    this.closeEditor(sender, rowIndex);
  }

  public saveHandler({
    sender,
    rowIndex,
    formGroup
  }: {
    sender: GridComponent;
    rowIndex: number;
    formGroup: UntypedFormGroup;
  }): void {
    const edited: { dueDate?: Date; order: number } = formGroup.value;
    const oldOrder = rowIndex + 1;
    const newOrder = edited.order;
    if (oldOrder != newOrder) {
      // reordered.
      let direction = 1;
      let from = oldOrder;
      let to = newOrder;
      if (oldOrder > newOrder) {
        direction = -1;
        const tmp = from;
        from = to;
        to = tmp;
      }

      for (let i = from; i <= to; i = i + 1) {
        if (i === oldOrder) {
          continue; // do not modify the item in question.
        }
        const idx = i - 1; // for 0-based index
        if (!this.steps[idx]) {
          // moved out of bounds
          edited.order = edited.order - direction; // back into bounds...
          continue;
        }
        this.steps[idx].order = this.steps[idx].order - direction;
      }
    }
    this.steps[rowIndex].dueDate = edited.dueDate;
    this.steps[rowIndex].order = edited.order;
    sender.closeRow(rowIndex);

    this.steps.sort((a, b) => {
      return a.order - b.order;
    });

    this.checkForErrors();
  }

  public abort(): void {
    this.navigate.toCirculation(this.circulationId);
  }

  public save(): void {
    if (this.hasErrors) {
      return;
    }

    this.logger.debug("saving..");
    const reorderData = this.steps.map((s) => {
      return {
        originalOrder: s.originalOrder,
        order: s.order,
        dueDate: s.dueDate,
        stepId: s.id
      };
    });
    this.stepFacade
      .reorder({
        circulationId: this.circulationId,
        steps: reorderData
      })
      .subscribe({
        next: (id) => {
          this.logger.debug("saved..");
          this.navigate.toCirculation(id);
        },
        error: (err) => {
          this.logger.debug("error..");
          this.notificationFacade.showError(
            "Error while reodering Steps. Please ask your administrator for help. " +
              err.error.message
          );
        }
      });
  }

  private checkForErrors(): void {
    this.logger.debug("checking for errors.");
    let minDate = this.minDueDate;
    this.hasErrors = false;
    for (const s of this.steps) {
      if (!s.dueDate) {
        // No dueDate - is okay
        continue;
      }

      if (DueDateInRangeValidator.isBefore(s.dueDate, minDate)) {
        s.validOrder.before = minDate;
        s.validOrder.beforeTxt = this.i18nService.formatDate(minDate, {
          date: "medium"
        });
        this.hasErrors = true;
      } else {
        s.validOrder.before = null;
        s.validOrder.beforeTxt = null;
        minDate = s.dueDate;
      }

      // "after" is never in error-state
      s.validOrder.after = null;
      s.validOrder.afterTxt = null;
    }
  }

  private closeEditor(
    grid: GridComponent,
    rowIndex: number = this.editedRowIndex
  ) {
    grid.closeRow(rowIndex);
    this.editedRowIndex = undefined;
    this.formGroup = undefined;
  }
}

interface SortableStep extends IStepInit {
  order: number;
  originalOrder: number;
  validOrder: {
    before?: Date;
    beforeTxt?: string;
    after?: Date;
    afterTxt?: string;
  };
}
